/*
 * /massage-intake/$patientId (page)
 */
import {
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Container,
	Page,
	Placeholder,
	toast,
} from "@emr/react-ui"
import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { trpc } from "../../trpc"
import { PatientHeader } from "../../features/patient/patient-header"
import { rolePageLoader } from "../../utils/auth.utils"
import type { Patient } from "@emr/db/client"

const MOCK_PATIENT: Partial<Patient> = {
	firstName: "Patient",
	lastName: "",
}

export const Route = createFileRoute("/massage-intake/$patientId")({
	component: MassageIntakePage,
	loader: rolePageLoader(["massage_therapist", "admin", "super_admin"]),
})

const PRESSURE_OPTIONS = [
	{ value: "light",  label: "Light" },
	{ value: "medium", label: "Medium" },
	{ value: "firm",   label: "Firm" },
	{ value: "deep",   label: "Deep" },
]

type PressureValue = "light" | "medium" | "firm" | "deep"

function MassageIntakePage() {
	const { patientId } = Route.useParams()

	const { data: patient } = trpc.patient.getByFilter.useQuery({ where: { id: patientId } })
	const { data: practitioners = [] } = trpc.practitioner.getAllByFilter.useQuery({ where: { active: true } })
	const { data: existing, isLoading, refetch } = trpc.massageIntake.getLatestForPatient.useQuery({ patientId })

	const displayPatient = patient ?? (MOCK_PATIENT as Patient)

	return (
		<Page centerVertical={false}>
			<PatientHeader patient={displayPatient} patientId={patientId} isDirty={false} />
			<Container width="xl" padding="lg" gap="lg">
				<div className="flex items-center justify-between">
					<div>
						<h2 className="text-lg font-semibold">Massage Intake Form</h2>
						{existing && (
							<p className="text-xs text-muted-foreground mt-0.5">
								Last updated:{" "}
								{new Date(existing.updatedAt).toLocaleDateString("en-CA", {
									year: "numeric", month: "short", day: "numeric",
								})}
							</p>
						)}
					</div>
				</div>

				{isLoading ? (
					<Placeholder title="Loading…" />
				) : (
					<IntakeForm
						patientId={patientId}
						practitioners={practitioners as any[]}
						existing={existing as any}
						onSaved={refetch}
					/>
				)}
			</Container>
		</Page>
	)
}

/* ─── Form ────────────────────────────────────────────────────────────────── */

function IntakeForm({
	patientId,
	practitioners,
	existing,
	onSaved,
}: {
	patientId: string
	practitioners: any[]
	existing: any
	onSaved: () => void
}) {
	const [therapistId, setTherapistId] = useState(existing?.therapistId ?? "")

	// Chief Complaint
	const [mainComplaint, setMainComplaint] = useState(existing?.mainComplaint ?? "")
	const [painLocation, setPainLocation] = useState(existing?.painLocation ?? "")
	const [painScale, setPainScale] = useState<string>(existing?.painScale?.toString() ?? "")
	const [painDuration, setPainDuration] = useState(existing?.painDuration ?? "")

	// Medical History
	const [medicalConditions, setMedicalConditions] = useState(existing?.medicalConditions ?? "")
	const [medications, setMedications] = useState(existing?.medications ?? "")
	const [allergies, setAllergies] = useState(existing?.allergies ?? "")
	const [recentSurgeries, setRecentSurgeries] = useState(existing?.recentSurgeries ?? "")

	// Massage History
	const [previousMassage, setPreviousMassage] = useState<boolean>(existing?.previousMassage ?? false)
	const [previousMassageNotes, setPreviousMassageNotes] = useState(existing?.previousMassageNotes ?? "")
	const [massageGoals, setMassageGoals] = useState(existing?.massageGoals ?? "")
	const [preferredPressure, setPreferredPressure] = useState<PressureValue | "">(existing?.preferredPressure ?? "")

	// Contraindications
	const [contraindications, setContraindications] = useState(existing?.contraindications ?? "")

	// Lifestyle
	const [occupation, setOccupation] = useState(existing?.occupation ?? "")
	const [stressLevel, setStressLevel] = useState<string>(existing?.stressLevel?.toString() ?? "")
	const [sleepQuality, setSleepQuality] = useState(existing?.sleepQuality ?? "")
	const [exerciseFrequency, setExerciseFrequency] = useState(existing?.exerciseFrequency ?? "")

	// Additional
	const [additionalNotes, setAdditionalNotes] = useState(existing?.additionalNotes ?? "")

	const upsert = trpc.massageIntake.upsert.useMutation({
		onSuccess: () => {
			onSaved()
			toast.success("Intake form saved")
		},
		onError: (e) => toast.error(e.message),
	})

	function handleSave() {
		upsert.mutate({
			patientId,
			therapistId: therapistId || undefined,
			mainComplaint: mainComplaint || null,
			painLocation: painLocation || null,
			painScale: painScale ? parseInt(painScale, 10) : null,
			painDuration: painDuration || null,
			medicalConditions: medicalConditions || null,
			medications: medications || null,
			allergies: allergies || null,
			recentSurgeries: recentSurgeries || null,
			previousMassage,
			previousMassageNotes: previousMassageNotes || null,
			massageGoals: massageGoals || null,
			preferredPressure: (preferredPressure || null) as PressureValue | null,
			contraindications: contraindications || null,
			occupation: occupation || null,
			stressLevel: stressLevel ? parseInt(stressLevel, 10) : null,
			sleepQuality: sleepQuality || null,
			exerciseFrequency: exerciseFrequency || null,
			additionalNotes: additionalNotes || null,
		})
	}

	return (
		<div className="space-y-6">
			{/* Therapist */}
			<Card>
				<CardHeader><CardTitle className="text-base">Therapist</CardTitle></CardHeader>
				<CardContent>
					<select
						value={therapistId}
						onChange={(e) => setTherapistId(e.target.value)}
						className="h-9 rounded-md border bg-background px-3 text-sm w-72"
					>
						<option value="">— Select therapist —</option>
						{practitioners.map((p) => (
							<option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>
						))}
					</select>
				</CardContent>
			</Card>

			{/* Chief Complaint */}
			<Card>
				<CardHeader><CardTitle className="text-base">Chief Complaint</CardTitle></CardHeader>
				<CardContent className="space-y-3">
					<FormField label="Main Complaint">
						<textarea
							value={mainComplaint}
							onChange={(e) => setMainComplaint(e.target.value)}
							rows={3}
							placeholder="Describe the primary reason for seeking massage therapy…"
							className="w-full rounded-md border bg-background px-3 py-2 text-sm resize-none"
						/>
					</FormField>
					<div className="grid sm:grid-cols-2 gap-3">
						<FormField label="Pain / Discomfort Location">
							<input
								type="text"
								value={painLocation}
								onChange={(e) => setPainLocation(e.target.value)}
								placeholder="e.g. lower back, neck, shoulders"
								className="w-full h-9 rounded-md border bg-background px-3 text-sm"
							/>
						</FormField>
						<FormField label="Pain Scale (0–10)">
							<input
								type="number"
								min="0"
								max="10"
								value={painScale}
								onChange={(e) => setPainScale(e.target.value)}
								className="w-full h-9 rounded-md border bg-background px-3 text-sm"
							/>
						</FormField>
						<FormField label="Duration of Symptoms">
							<input
								type="text"
								value={painDuration}
								onChange={(e) => setPainDuration(e.target.value)}
								placeholder="e.g. 3 weeks, ongoing"
								className="w-full h-9 rounded-md border bg-background px-3 text-sm"
							/>
						</FormField>
					</div>
				</CardContent>
			</Card>

			{/* Medical History */}
			<Card>
				<CardHeader><CardTitle className="text-base">Medical History</CardTitle></CardHeader>
				<CardContent className="space-y-3">
					<FormField label="Medical Conditions">
						<textarea
							value={medicalConditions}
							onChange={(e) => setMedicalConditions(e.target.value)}
							rows={2}
							placeholder="List any diagnosed conditions…"
							className="w-full rounded-md border bg-background px-3 py-2 text-sm resize-none"
						/>
					</FormField>
					<FormField label="Current Medications">
						<textarea
							value={medications}
							onChange={(e) => setMedications(e.target.value)}
							rows={2}
							placeholder="List current medications and dosages…"
							className="w-full rounded-md border bg-background px-3 py-2 text-sm resize-none"
						/>
					</FormField>
					<FormField label="Allergies">
						<input
							type="text"
							value={allergies}
							onChange={(e) => setAllergies(e.target.value)}
							placeholder="Known allergies (oils, lotions, latex, etc.)"
							className="w-full h-9 rounded-md border bg-background px-3 text-sm"
						/>
					</FormField>
					<FormField label="Recent Surgeries or Injuries">
						<input
							type="text"
							value={recentSurgeries}
							onChange={(e) => setRecentSurgeries(e.target.value)}
							placeholder="Describe any recent surgeries or injuries"
							className="w-full h-9 rounded-md border bg-background px-3 text-sm"
						/>
					</FormField>
				</CardContent>
			</Card>

			{/* Massage History & Goals */}
			<Card>
				<CardHeader><CardTitle className="text-base">Massage History &amp; Goals</CardTitle></CardHeader>
				<CardContent className="space-y-3">
					<div className="flex items-center gap-2">
						<input
							type="checkbox"
							id="previousMassage"
							checked={previousMassage}
							onChange={(e) => setPreviousMassage(e.target.checked)}
							className="w-4 h-4"
						/>
						<label htmlFor="previousMassage" className="text-sm">
							Has received professional massage therapy before
						</label>
					</div>
					{previousMassage && (
						<FormField label="Previous Massage Notes">
							<input
								type="text"
								value={previousMassageNotes}
								onChange={(e) => setPreviousMassageNotes(e.target.value)}
								placeholder="Frequency, type, reactions…"
								className="w-full h-9 rounded-md border bg-background px-3 text-sm"
							/>
						</FormField>
					)}
					<FormField label="Goals for Massage Therapy">
						<textarea
							value={massageGoals}
							onChange={(e) => setMassageGoals(e.target.value)}
							rows={2}
							placeholder="e.g. pain relief, relaxation, improved mobility…"
							className="w-full rounded-md border bg-background px-3 py-2 text-sm resize-none"
						/>
					</FormField>
					<FormField label="Preferred Pressure">
						<div className="flex gap-3 flex-wrap">
							{PRESSURE_OPTIONS.map((opt) => (
								<label key={opt.value} className="flex items-center gap-1.5 text-sm cursor-pointer">
									<input
										type="radio"
										name="pressure"
										value={opt.value}
										checked={preferredPressure === opt.value}
										onChange={() => setPreferredPressure(opt.value as PressureValue)}
									/>
									{opt.label}
								</label>
							))}
						</div>
					</FormField>
				</CardContent>
			</Card>

			{/* Contraindications */}
			<Card>
				<CardHeader><CardTitle className="text-base">Contraindications</CardTitle></CardHeader>
				<CardContent>
					<FormField label="Known Contraindications">
						<textarea
							value={contraindications}
							onChange={(e) => setContraindications(e.target.value)}
							rows={2}
							placeholder="Areas to avoid, conditions requiring caution…"
							className="w-full rounded-md border bg-background px-3 py-2 text-sm resize-none"
						/>
					</FormField>
				</CardContent>
			</Card>

			{/* Lifestyle */}
			<Card>
				<CardHeader><CardTitle className="text-base">Lifestyle</CardTitle></CardHeader>
				<CardContent className="space-y-3">
					<div className="grid sm:grid-cols-2 gap-3">
						<FormField label="Occupation">
							<input
								type="text"
								value={occupation}
								onChange={(e) => setOccupation(e.target.value)}
								placeholder="Job title / nature of work"
								className="w-full h-9 rounded-md border bg-background px-3 text-sm"
							/>
						</FormField>
						<FormField label="Stress Level (0–10)">
							<input
								type="number"
								min="0"
								max="10"
								value={stressLevel}
								onChange={(e) => setStressLevel(e.target.value)}
								className="w-full h-9 rounded-md border bg-background px-3 text-sm"
							/>
						</FormField>
						<FormField label="Sleep Quality">
							<input
								type="text"
								value={sleepQuality}
								onChange={(e) => setSleepQuality(e.target.value)}
								placeholder="e.g. poor, fair, good"
								className="w-full h-9 rounded-md border bg-background px-3 text-sm"
							/>
						</FormField>
						<FormField label="Exercise Frequency">
							<input
								type="text"
								value={exerciseFrequency}
								onChange={(e) => setExerciseFrequency(e.target.value)}
								placeholder="e.g. daily, 3x/week, rarely"
								className="w-full h-9 rounded-md border bg-background px-3 text-sm"
							/>
						</FormField>
					</div>
				</CardContent>
			</Card>

			{/* Additional */}
			<Card>
				<CardHeader><CardTitle className="text-base">Additional Notes</CardTitle></CardHeader>
				<CardContent>
					<textarea
						value={additionalNotes}
						onChange={(e) => setAdditionalNotes(e.target.value)}
						rows={3}
						placeholder="Any other relevant information…"
						className="w-full rounded-md border bg-background px-3 py-2 text-sm resize-none"
					/>
				</CardContent>
			</Card>

			<div className="flex justify-end">
				<Button onClick={handleSave} disabled={upsert.isPending} size="lg">
					{upsert.isPending ? "Saving…" : "Save Intake Form"}
				</Button>
			</div>
		</div>
	)
}

/* ─── FormField ──────────────────────────────────────────────────────────── */

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div className="space-y-1">
			<label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</label>
			{children}
		</div>
	)
}
